import React, { Component } from 'react'
import { Howl } from 'howler'
import PropTypes from 'prop-types'
import AbcRenderer from '../AbcRenderer'

import { getRandomValueFromArray } from '../Functions'
import BaseSupSub from './BaseSupSub'

import click3SoundFileMP3 from '../sounds/click3.mp3'
import click3SoundFileOGG from '../sounds/click3.ogg'
import click3SoundFileAAC from '../sounds/click3.aac'

import click2SoundFileMP3 from '../sounds/click2.mp3'
import click2SoundFileOGG from '../sounds/click2.ogg'
import click2SoundFileAAC from '../sounds/click2.aac'

import click1SoundFileMP3 from '../sounds/click1.mp3'
import click1SoundFileOGG from '../sounds/click1.ogg'
import click1SoundFileAAC from '../sounds/click1.aac'

const Metronome = (props) => (
  <div id="metronome" className={props.className}>
    <div id="bpm">{props.bpm} BPM</div>
    <div id="clicks">    
      { [...Array(4)].map((v, i) =>
          <div className={ 'quarter-note' + (props.quarterNote === i+1 ? ' active' : '') } key={i+1}>{i+1}</div>) }
    </div>
  </div>
)

const SequenceRealtime = (props) => (
  <div id="sequence-rt" className={props.className + (props.type === 'abc' ? ' abctype' : ' strtype')}>
    { props.type === 'string' && (
      <ul className="str-container">
        <li id="current"><BaseSupSub data={ { ...props.current } }/></li>
        <li id="next">( <BaseSupSub data={ { ...props.next } }/> )</li>
      </ul>
    )}
    { props.type === 'abc' && (
      <div className="abc-container">
        <div id="abc-sequence"></div>
        <div id="abc-error"></div>
      </div>
    )}
  </div>
)

const SequenceStatic = (props) => (
  <div id="sequence-st" className={props.className + (props.type === 'abc' ? ' abctype' : ' strtype')}>
    { props.type === 'string' && (
      <ul className="str-container">
        {props.sequence.map((v, i) =>
          <li className={ props.bar === i+1 ? 'active' : '' } key={i}>
            <BaseSupSub data={ { ...v } }/>
          </li>
        )}
      </ul>
    )}
    { props.type === 'abc' && (
      <div className="abc-container">
        <div id="abc-sequence"></div>
        <div id="abc-error"></div>
      </div>
    )}
  </div>
)

class Sequence extends Component {

  constructor(props) {
    super(props)

    this.getItem = this.setGetItem(this.props)

    this.state = {
      quarterNote: 1,
      barRep: 1,
      bar: 1,
      sequence: this.createSequence(this.props.nBars, this.props.barReps),
      current: this.props.realtime ? this.getItem() : null,
      next: this.props.realtime ? this.getItem(): null
    }

    this.clickSounds = [
      new Howl({
        src: [click1SoundFileMP3, click1SoundFileOGG, click1SoundFileAAC],
        preload: true
      }),
      new Howl({
        src: [click2SoundFileMP3, click2SoundFileOGG, click2SoundFileAAC],
        preload: true
      }),
      new Howl({
        src: [click3SoundFileMP3, click3SoundFileOGG, click3SoundFileAAC],
        preload: true
      })
    ]

    if (this.props.bases.type === 'abc')
      this.abcRenderer = new AbcRenderer()
  }

  setGetItem = (props) => {
    return () => ({ 
      base: props.bases.groups ?
              [...Array(+props.bases.groups)].map(v => getRandomValueFromArray(props.basesSel)()).join(' ') :
              getRandomValueFromArray(props.basesSel)(),
      sup: props.sups.groups ?
             [...Array(+props.sups.groups)].map(v => getRandomValueFromArray(props.supsSel)()).join(' ') :
             getRandomValueFromArray(props.supsSel)(),
      sub: props.subs.groups ?
             [...Array(+props.subs.groups)].map(v => getRandomValueFromArray(props.subsSel)()).join(' ') :
             getRandomValueFromArray(props.subsSel)()
    });
  }

  createSequence = (nBars, barReps, elFunc=this.getItem) => {
    return [...Array(nBars)]
             .map(elFunc)
             .reduce((v1, v2) => v1.concat(Array(barReps).fill(v2)),[])
  }

  resetSequence = (realtime, nBars, barReps) => {
    this.setState({
      quarterNote: 1,
      barRep: 1,
      bar: 1,
      sequence: realtime ? null : this.createSequence(nBars, barReps),
      current: realtime ? this.getItem() : null,
      next: realtime ? this.getItem(): null
    })
  }

  update = () => {
    this.setState((prevState, props) => {

      let newState = prevState.quarterNote === 4 ? (
        this.props.realtime ? (
          prevState.barRep === this.props.barReps ? (
            { quarterNote: 1, barRep: 1, current: { ...prevState.next }, next: this.getItem() }
          ) : (
            { quarterNote: 1, barRep: prevState.barRep + 1 }
          )
        ) : (
          { quarterNote: 1, bar: prevState.bar < (this.props.nBars*this.props.barReps) ? prevState.bar+1 : 1 }
        )
      ) : (
        { quarterNote: prevState.quarterNote + 1  }
      )

      if (this.props.sound) {
        if (prevState.quarterNote === 4)
          this.clickSounds[2].play()
        else
          this.clickSounds[1].play()
      }

      return newState
    })
  }

  renderAbcNotation = () => {
    
    let abcTune = this.props.realtime ?
      this.tuneAbcNotation(this.state.current.base) + (this.props.barsPerLine === 1 ? ' |\n' : ' | ') + this.tuneAbcNotation(this.state.next.base) + ' |\n'
    :
      this.state.sequence.map((v,i) => (i>=this.props.barsPerLine && i%this.props.barsPerLine===0 ? '\n' : '') + this.tuneAbcNotation(v.base)).join(' | ') + ' |\n'

    this.abcRenderer.renderAbc(this.props.abcScale, abcTune, 'abc-sequence', 'abc-error')
  }

  tuneAbcNotation = (abcNotation) => {
    if (abcNotation === 'z4 z4 z4 z4')
      return 'z16'
    else if (abcNotation.substr(0,5) === 'z4 z4') {
      return 'z8' + abcNotation.substr(5)
    }
    else if (abcNotation.substr(-5) === 'z4 z4') {
      return abcNotation.slice(0,-5) + 'z8'
    }
    else {
      return abcNotation
    }
  }

  windowResize = () => {
    if (this.props.bases.type === 'abc')
      this.renderAbcNotation()
  }

  componentWillMount() {
    if (this.props.play) {
      if (this.props.sound)
        this.clickSounds[2].play()
      
      this.timerID = setInterval(this.update, Math.floor(60000/this.props.bpm))
    }
  }

  componentWillUnmount() {
    clearInterval(this.timerID)
    window.removeEventListener('resize', this.windowResize)
  }

  componentDidMount() {
    window.addEventListener('resize', this.windowResize)
    if (this.props.bases.type === 'abc')
      this.renderAbcNotation()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.bases.type === 'abc' &&
        (this.state.sequence !== prevState.sequence || this.state.current !== prevState.current || this.state.next !== prevState.next))
      this.renderAbcNotation()
  }

  componentWillReceiveProps(nextProps) {
    
    if (nextProps.play !== this.props.play) {
      if (nextProps.play)
        this.timerID = setInterval(this.update, Math.floor(60000/nextProps.bpm))
      else
        clearInterval(this.timerID)
    }
    else if (nextProps.gameInfo !== this.props.gameInfo ||
             nextProps.metronome !== this.props.metronome ||
             nextProps.sound !== this.props.sound) {
    }
    else if (nextProps.bpm !== this.props.bpm) {
      if (nextProps.play) {
        clearInterval(this.timerID)
        if (!Number.isNaN(nextProps.bpm) && nextProps.bpm >= 1)
          this.timerID = setInterval(this.update, Math.floor(60000/nextProps.bpm))
      }
    }
    else if ( nextProps.basesSel !== this.props.basesSel ||
              nextProps.supsSel !== this.props.supsSel ||
              nextProps.subsSel !== this.props.subsSel ) {
      this.getItem = this.setGetItem(nextProps)
      this.resetSequence(nextProps.realtime, nextProps.nBars, nextProps.barReps)
      if (nextProps.play) {
        clearInterval(this.timerID)
        this.timerID = setInterval(this.update, Math.floor(60000/nextProps.bpm))
      }
    }
    else {
      this.resetSequence(nextProps.realtime, nextProps.nBars, nextProps.barReps)
      if (nextProps.play) {
        clearInterval(this.timerID)
        this.timerID = setInterval(this.update, Math.floor(60000/nextProps.bpm))
      }
    }
  }

  render() {
    
    const { gameName, metronome, bpm, realtime, bases } = this.props
    const { quarterNote, bar, sequence, current, next } = this.state

    return (
      <div id="sequence">
        <div id="game-title">{gameName}</div>
        {metronome && (
          <Metronome
            bpm={bpm}
            quarterNote={quarterNote}
          />
        )}
        {realtime ? (
          <SequenceRealtime
            className="seq-container"
            type={bases.type}
            current={current} 
            next={next}
          />
        ) : (
          <SequenceStatic
            className="seq-container"
            type={bases.type}
            bar={bar}
            sequence={sequence}
          />
        )}
      </div>
    );
  }
}

Sequence.propTypes = {
  bases: PropTypes.object,
  sups: PropTypes.object,
  subs: PropTypes.object,
  realtime: PropTypes.bool,
  play: PropTypes.bool,
  metronome: PropTypes.bool,
  sound: PropTypes.bool,
  nBars: PropTypes.number,
  barReps: PropTypes.number,
  barsPerLine: PropTypes.number,
  abcScale: PropTypes.number,
  bpm: PropTypes.number,
  basesSel: PropTypes.array.isRequired,
  supsSel: PropTypes.array,
  subsSel: PropTypes.array
}

Sequence.defaultProps = {
  bases: { type: 'string', options: {} },
  sups: { options: {} },
  subs: { options: {} },
  realtime: false,
  play: true,
  metronome: true,
  sound: false,
  nBars: 1,
  barReps: 1,
  barsPerLine: 4,
  abcScale: 1,
  bpm: 80,
  basesSel: [0],
  supsSel: [],
  subsSel: []
}

export default Sequence


// WEBPACK FOOTER //
// ./src/components/Sequence.js